import { IObjectStringKeyMap } from '@shared/models';
import type { HealthEvent, StateEvent } from '@services/assets/asset.interfaces';

/**
 * asset:inUse readings. A reading is in use (1) or not in use (0); devices send 1/0, manual
 * entry can send strings or a { value } object. Mirrors rest-api Properties::stateFromValue so
 * the webapp and the server read the same reading the same way.
 */
export const IN_USE_STATE_KEY = 'PROPERTY.State_InUse';
export const NOT_IN_USE_STATE_KEY = 'PROPERTY.State_NotInUse';

/** Badge / state-history colour and label per state, shaped like PropertyService.aiStatuses. */
export const IN_USE_STATUSES: { [state: number]: { type: string; title: string } } = {
  1: { type: 'green', title: IN_USE_STATE_KEY },
  0: { type: 'neutral', title: NOT_IN_USE_STATE_KEY }
};

export function isInUseReading(reading: unknown): boolean {
  if (typeof reading === 'string' && /^\s*\{/.test(reading)) {
    try {
      reading = JSON.parse(reading);
    } catch {
      return false;
    }
  }
  if (reading !== null && typeof reading === 'object') {
    // a { value } reading unwraps; any other object or array (e.g. a note-only entry) is not a reading
    return !Array.isArray(reading) && 'value' in reading && isInUseReading((reading as { value: unknown }).value);
  }
  if (typeof reading === 'string') {
    // the string 'false' is truthy to JavaScript; treat the usual off spellings and '' as not in use
    return !/^\s*(false|off|no|0*\.?0*)\s*$/i.test(reading);
  }
  return !!reading;
}

export function inUseState(reading: unknown): 0 | 1 {
  return isInUseReading(reading) ? 1 : 0;
}

/**
 * getAssets stateEvents, keyed by propertyID, in the HealthEvent shape the state-history and
 * reporting code builds periods from: the state (1 / 0) rides in `health`.
 */
export function stateEventsAsHealthEvents(stateEvents: IObjectStringKeyMap<StateEvent[]>): IObjectStringKeyMap<HealthEvent[]> {
  const result: IObjectStringKeyMap<HealthEvent[]> = {};
  for (const propertyID of Object.keys(stateEvents ?? {})) {
    result[propertyID] = (stateEvents[propertyID] ?? []).map((event) => ({
      id: event.id,
      health: event.state,
      metric: event.state,
      time: event.time,
      workTimeSeconds: event.workTimeSeconds || 0,
      clockTimeSeconds: event.clockTimeSeconds || 0
    }));
  }
  return result;
}
